import { literalType } from "../../../api/content/processTerms";
import type { WorkflowInputSummary, WorkflowSummary } from "../../../api/content/workflowsApi";

/**
 * The declared inputs of exactly the Workflow version a start names, among a content version's listed Workflows;
 * `null` where that version isn't listed. A fork onto a newer content version keeps its parent's start, and the
 * Workflow's newer version may label, key or type its inputs otherwise (Sol r1 S1): that start then stays a line.
 */
export function startInputsDeclared(workflows: readonly WorkflowSummary[] | null, workflow: string): readonly WorkflowInputSummary[] | null {
  return workflows?.find((listed) => listed.reference === workflow)?.inputs ?? null;
}

/** One text a person wrote into a Run's start form, under the input's label. */
export interface StartTextInput {
  readonly destination: string;
  readonly label: string;
  readonly text: string;
}

/** A later step's own input as its step declares it: its label in the reader's language, and the types it accepts. */
export interface DeclaredStepInput {
  readonly label: string;
  readonly accepts: readonly { readonly type: string }[];
}

/**
 * What the person who started a Run wrote into its start form (Johannes, 4 Nov 15:36): the start's values, stored by
 * destination, that fill an input declared for text only, each with its label: first the Workflow's own inputs, in
 * the order it declares them, then what the form asked for a later step's own input (`stepInput`, by that step's
 * declaration; the 16:32 list, item 2). Item references, numbers and the subject are not words a person wrote, so
 * they stay out, as do values nothing declares and empty ones. `declared` is `null` while the Workflow's inputs
 * aren't known.
 */
export function startTextInputs(
  values: Readonly<Record<string, unknown>> | undefined,
  declared: readonly WorkflowInputSummary[] | null,
  stepInput: (destination: string) => DeclaredStepInput | null = () => null,
): readonly StartTextInput[] {
  if (values === undefined || declared === null) return [];
  const text = literalType("string");
  const said = (destination: string, label: string, accepts: readonly { readonly type: string }[]): StartTextInput[] => {
    const value = values[destination];
    const takesText = accepts.length > 0 && accepts.every((accepted) => accepted.type === text);
    return takesText && typeof value === "string" && value.trim() !== "" ? [{ destination, label, text: value }] : [];
  };
  const own = new Set(declared.map((input) => input.destination));
  const asked = Object.keys(values).filter((destination) => !own.has(destination));
  return [
    ...declared.flatMap((input) => said(input.destination, input.label, input.accepts)),
    ...asked.flatMap((destination) => {
      const input = stepInput(destination);
      return input === null ? [] : said(destination, input.label, input.accepts);
    }),
  ];
}
